import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  formatEuro,
  type Buyer,
  type PricePreview,
  type PublicEvent,
  type PublicOrderView,
} from '@avh/shared';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Feedback';
import { Checkbox, SelectField, TextField } from '@/components/ui/Field';
import { useI18n } from '@/contexts/I18n';
import { ApiRequestError, apiRequest, errorMessage } from '@/lib/api';

type BuyerField = keyof Buyer;

const EMPTY_BUYER: Buyer = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  zip: '',
  city: '',
  country: 'Österreich',
};

type SubmitState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string };

/** Feldfehler aus der API (`buyer.email` → `email`). */
function fieldErrorsFrom(error: unknown): Partial<Record<BuyerField, string>> {
  if (!(error instanceof ApiRequestError) || !Array.isArray(error.details)) {
    return {};
  }
  return Object.fromEntries(
    (error.details as { path: string; message: string }[])
      .filter((detail) => detail.path.startsWith('buyer.'))
      .map((detail) => [detail.path.slice('buyer.'.length), detail.message]),
  );
}

export function TicketForm({ event }: { event: PublicEvent }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const isFree = event.priceCents === 0;
  const maxQuantity = Math.max(Math.min(event.maxPerOrder, event.available), 1);

  // Ein Schlüssel pro Formular: mehrfaches Absenden erzeugt garantiert nur eine Bestellung.
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [quantity, setQuantity] = useState(1);
  const [codeInput, setCodeInput] = useState('');
  const [discount, setDiscount] = useState<PricePreview | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [buyer, setBuyer] = useState<Buyer>(EMPTY_BUYER);
  const [accepted, setAccepted] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [submit, setSubmit] = useState<SubmitState>({ status: 'idle' });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<BuyerField, string>>>({});

  const discountPerTicket = discount?.discountPerTicketCents ?? 0;
  const total = quantity * (event.priceCents - discountPerTicket);

  function updateBuyer(field: BuyerField, value: string) {
    setBuyer((current) => ({ ...current, [field]: value }));
  }

  async function applyCode() {
    setCodeError(null);
    try {
      const preview = await apiRequest<PricePreview>(`/public/events/${event.id}/price`, {
        method: 'POST',
        body: { quantity, discountCode: codeInput.trim() },
      });
      setDiscount(preview);
    } catch (error) {
      setDiscount(null);
      setCodeError(errorMessage(error));
    }
  }

  async function handleSubmit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (submit.status === 'submitting') {
      return;
    }
    setSubmit({ status: 'submitting' });
    setFieldErrors({});
    try {
      const order = await apiRequest<PublicOrderView>('/public/checkout', {
        method: 'POST',
        body: {
          eventId: event.id,
          quantity,
          discountCode: discount?.discountCode ?? undefined,
          buyer,
          locale,
          idempotencyKey,
          acceptTerms: accepted,
          website: honeypot || undefined,
        },
      });
      navigate(`/tickets/bestellung/${order.id}?k=${encodeURIComponent(order.accessKey)}`, { replace: true, state: { justOrdered: true } });
    } catch (error) {
      setFieldErrors(fieldErrorsFrom(error));
      setSubmit({ status: 'error', message: errorMessage(error) });
    }
  }

  const field = (name: BuyerField) => ({
    value: buyer[name],
    onChange: (changeEvent: React.ChangeEvent<HTMLInputElement>) => updateBuyer(name, changeEvent.target.value),
    error: fieldErrors[name],
  });

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate={false}>
      <SelectField
        label={t.event.quantity}
        value={quantity}
        onChange={(changeEvent) => {
          setQuantity(Number(changeEvent.target.value));
          setDiscount(null);
        }}
      >
        {Array.from({ length: maxQuantity }, (_, index) => index + 1).map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </SelectField>

      {isFree ? null : (
        <div className="flex items-end gap-2">
          <TextField
            label={t.event.discountCode}
            value={codeInput}
            onChange={(changeEvent) => {
              setCodeInput(changeEvent.target.value.toUpperCase());
              setDiscount(null);
              setCodeError(null);
            }}
            error={codeError ?? undefined}
            autoComplete="off"
            containerClassName="flex-1"
          />
          <Button variant="secondary" onClick={applyCode} disabled={codeInput.trim().length < 3} className="mb-[1px]">
            {t.event.applyCode}
          </Button>
        </div>
      )}
      {discount?.discountLabel ? <Alert tone="success">{t.event.codeApplied(discount.discountLabel)}</Alert> : null}

      {isFree ? null : (
        <dl className="rounded-md bg-paper px-4 py-3 text-sm">
          <div className="flex justify-between">
            <dt>
              {quantity} × {formatEuro(event.priceCents, locale)}
            </dt>
            <dd>{formatEuro(quantity * event.priceCents, locale)}</dd>
          </div>
          {discountPerTicket > 0 ? (
            <div className="flex justify-between text-brand-700">
              <dt>{t.event.discount}</dt>
              <dd>−{formatEuro(quantity * discountPerTicket, locale)}</dd>
            </div>
          ) : null}
          <div className="mt-2 flex justify-between border-t border-neutral-300 pt-2 text-base font-bold">
            <dt>{t.event.total}</dt>
            <dd>{formatEuro(total, locale)}</dd>
          </div>
        </dl>
      )}

      <fieldset className="flex flex-col gap-4">
        <legend className="mb-1 font-bold text-brand-900">{t.event.contact}</legend>
        <div className="grid grid-cols-2 gap-3">
          <TextField label={t.event.firstName} autoComplete="given-name" required {...field('firstName')} />
          <TextField label={t.event.lastName} autoComplete="family-name" required {...field('lastName')} />
        </div>
        <TextField label={t.event.email} type="email" autoComplete="email" required {...field('email')} />
        {isFree ? null : (
          <>
            <TextField label={t.event.phone} type="tel" autoComplete="tel" required {...field('phone')} />
            <TextField
              label={t.event.street}
              autoComplete="street-address"
              required
              hint={t.event.addressHint}
              {...field('street')}
            />
            <div className="grid grid-cols-[6rem_1fr] gap-3">
              <TextField label={t.event.zip} autoComplete="postal-code" inputMode="numeric" required {...field('zip')} />
              <TextField label={t.event.city} autoComplete="address-level2" required {...field('city')} />
            </div>
            <TextField label={t.event.country} autoComplete="country-name" required {...field('country')} />
          </>
        )}
      </fieldset>

      {/* Honeypot für Bots – für Menschen unsichtbar */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>
          Website
          <input tabIndex={-1} autoComplete="off" value={honeypot} onChange={(changeEvent) => setHoneypot(changeEvent.target.value)} />
        </label>
      </div>

      <Checkbox checked={accepted} onChange={(changeEvent) => setAccepted(changeEvent.target.checked)} required>
        {t.event.acceptPrefix}{' '}
        <a href="/ticketbedingungen/" target="_blank" className="font-semibold text-brand-600 underline">
          {t.event.terms}
        </a>{' '}
        {t.event.acceptMiddle}{' '}
        <a href="/datenschutz/" target="_blank" className="font-semibold text-brand-600 underline">
          {t.event.privacy}
        </a>
        {t.event.acceptSuffix === '.' ? '.' : ` ${t.event.acceptSuffix}`}
      </Checkbox>

      {submit.status === 'error' ? <Alert tone="error">{submit.message}</Alert> : null}

      <Button type="submit" size="lg" disabled={!accepted || submit.status === 'submitting'}>
        {submit.status === 'submitting' ? t.event.submitting : isFree ? t.event.submitRegistration : t.event.submitOrder}
      </Button>
      <p className="text-xs text-muted">{isFree ? t.event.freeInfo : t.event.paymentInfo}</p>
    </form>
  );
}
